// SPDX-License-Identifier: AGPL-3.0-or-later

import {MessageComponentSelect} from '@app/features/channel/components/MessageComponentSelect';
import styles from '@app/features/channel/components/MessageComponents.module.css';
import {MessageComponentsV2} from '@app/features/channel/components/MessageComponentsV2';
import {clickMessageComponent} from '@app/features/messaging/commands/MessageComponentCommands';
import {ReactionImage} from '@app/features/messaging/components/ReactionImage';
import type {Message} from '@app/features/messaging/models/MessagingMessage';
import {openExternalUrlWithWarning} from '@app/features/messaging/utils/ExternalLinkUtils';
import {useEmojiURL} from '@app/features/messaging/utils/ReactionUtils';
import {Button, type ButtonVariant} from '@app/features/ui/button/Button';
import {Tooltip} from '@app/features/ui/tooltip/Tooltip';
import {ButtonStyles, MessageComponentTypes} from '@fluxer/constants/src/MessageComponentConstants';
import type {MessageActionRow} from '@fluxer/schema/src/domains/message/MessageComponentSchemas';
import {useLingui} from '@lingui/react/macro';
import {ArrowSquareOutIcon} from '@phosphor-icons/react';
import {clsx} from 'clsx';
import {observer} from 'mobx-react-lite';
import {type ReactNode, useState} from 'react';

type RowItem = MessageActionRow['components'][number];
type MessageButton = Extract<RowItem, {type: 2}>;

const LINK_STYLE = 5;
const SELECT_TYPES: ReadonlySet<number> = new Set([3, 5, 6, 7, 8]);

const VARIANTS: Record<number, ButtonVariant> = {
	[ButtonStyles.PRIMARY]: 'primary',
	[ButtonStyles.SECONDARY]: 'secondary',
	[ButtonStyles.SUCCESS]: 'primary',
	[ButtonStyles.DANGER]: 'danger',
	[LINK_STYLE]: 'secondary',
};

function linkHostname(url: string | null | undefined): string | null {
	if (!url) return null;
	try {
		const parsed = new URL(url);
		return parsed.protocol === 'https:' || parsed.protocol === 'http:' ? parsed.hostname : null;
	} catch {
		return null;
	}
}

function ButtonEmoji({emoji}: {emoji: NonNullable<MessageButton['emoji']>}) {
	const url = useEmojiURL({emoji: {...emoji, animated: emoji.animated ?? false}});
	if (!url) return null;
	return (
		<ReactionImage
			src={url}
			alt={emoji.name}
			aria-hidden={true}
			draggable={false}
			className={styles.emoji}
			data-flx="channel.message-components.button-emoji.emoji"
		/>
	);
}

export function MessageComponentButton({
	message,
	button,
	isPreview,
}: {
	message: Message;
	button: MessageButton;
	isPreview: boolean;
}) {
	const {i18n} = useLingui();
	const [pending, setPending] = useState(false);
	const isLink = button.style === LINK_STYLE;
	const hostname = isLink ? linkHostname(button.url) : null;
	if (isLink ? !hostname : !button.custom_id) return null;
	const handleClick = async () => {
		if (isLink) {
			openExternalUrlWithWarning(button.url!);
			return;
		}
		setPending(true);
		try {
			await clickMessageComponent(i18n, message, button.custom_id!);
		} finally {
			setPending(false);
		}
	};
	const element = (
		<Button
			variant={VARIANTS[button.style] ?? 'secondary'}
			small
			fitContent
			className={clsx(
				styles.button,
				button.style === ButtonStyles.SUCCESS && styles.success,
				button.style === ButtonStyles.SECONDARY && styles.secondary,
				button.style === ButtonStyles.DANGER && styles.danger,
				isLink && styles.link,
			)}
			contentClassName={styles.content}
			disabled={isPreview || Boolean(button.disabled)}
			submitting={pending}
			aria-busy={pending || undefined}
			leftIcon={
				button.emoji ? (
					<ButtonEmoji
						emoji={button.emoji}
						data-flx="channel.message-components.message-component-button.button-emoji"
					/>
				) : undefined
			}
			rightIcon={
				isLink ? (
					<ArrowSquareOutIcon
						weight="bold"
						className={styles.linkIcon}
						aria-hidden={true}
						data-flx="channel.message-components.message-component-button.arrow-square-out-icon"
					/>
				) : undefined
			}
			aria-label={button.label || button.emoji?.name}
			onClick={() => void handleClick()}
			data-flx="channel.message-components.message-component-button.button"
		>
			{button.label ? (
				<span className={styles.label} data-flx="channel.message-components.message-component-button.label">
					{button.label}
				</span>
			) : null}
		</Button>
	);
	return isLink ? (
		<Tooltip text={hostname!} data-flx="channel.message-components.message-component-button.tooltip">
			{element}
		</Tooltip>
	) : (
		element
	);
}

function isKnownRowItem(component: {type: number}): boolean {
	return component.type === MessageComponentTypes.BUTTON || SELECT_TYPES.has(component.type);
}

function renderRowItem(message: Message, component: RowItem, isPreview: boolean, key: number): ReactNode {
	if (component.type === MessageComponentTypes.BUTTON) {
		return (
			<MessageComponentButton
				key={key}
				message={message}
				button={component}
				isPreview={isPreview}
				data-flx="channel.message-components.render-row-item.message-component-button"
			/>
		);
	}
	if (SELECT_TYPES.has(component.type)) {
		return (
			<MessageComponentSelect
				key={key}
				message={message}
				select={component}
				isPreview={isPreview}
				data-flx="channel.message-components.render-row-item.message-component-select"
			/>
		);
	}
	return null;
}

function ComponentRow({message, row, isPreview}: {message: Message; row: MessageActionRow; isPreview: boolean}) {
	if (!row.components.some(isKnownRowItem)) return null;
	return (
		<div role="group" className={styles.row} data-flx="channel.message-components.row">
			{row.components.map((component, index) => renderRowItem(message, component, isPreview, index))}
		</div>
	);
}

export const MessageComponents = observer(({message, isPreview}: {message: Message; isPreview: boolean}) => {
	if (message.components.length === 0) return null;
	if (message.isComponentsV2) {
		return (
			<MessageComponentsV2
				message={message}
				isPreview={isPreview}
				renderRow={(row, key) => (
					<ComponentRow
						key={key}
						message={message}
						row={row}
						isPreview={isPreview}
						data-flx="channel.message-components.message-action-row--2"
					/>
				)}
				renderAccessory={(component) => renderRowItem(message, component, isPreview, 0)}
				data-flx="channel.message-components.message-components-v2"
			/>
		);
	}
	const rows = message.components.filter(
		(component): component is MessageActionRow =>
			component.type === MessageComponentTypes.ACTION_ROW && component.components.some(isKnownRowItem),
	);
	if (rows.length === 0) return null;
	return (
		<div className={styles.components} data-flx="channel.message-components.components">
			{rows.map((row, rowIndex) => (
				<ComponentRow
					key={rowIndex}
					message={message}
					row={row}
					isPreview={isPreview}
					data-flx="channel.message-components.message-action-row"
				/>
			))}
		</div>
	);
});
